import type { LogsBeforeSend } from '@datadog/browser-logs';
import type { RumBeforeSend, RumErrorEvent } from '@datadog/browser-rum';

import { attributeTelemetry, FIRST_PARTY } from './telemetry-attribution';
import { redactText, redactUrl } from './telemetry-redaction';

/**
 * `beforeSend` hooks for the RUM and Logs SDKs (see `monitoring.lib.ts`): redact user-entered
 * values from every event and tag errors/logs with a first/third-party `attribution`.
 *
 * Kept attributes: route (`view.name`, URL path), method, status, service, env and version.
 * Query in Datadog: `@context.attribution.party:third_party` (RUM), `@attribution.party:third_party`
 * (logs).
 */

type Fields = Record<string, unknown>;

const asFields = (value: unknown): Fields | undefined =>
  value !== null && typeof value === 'object' ? (value as Fields) : undefined;

const rewrite = (holder: unknown, key: string, redact: (value: string) => string) => {
  const fields = asFields(holder);
  const value = fields?.[key];
  if (fields && typeof value === 'string') {
    fields[key] = redact(value);
  }
};

const attributeRumError = (error: RumErrorEvent['error']) =>
  // `custom` errors come from our own `datadogRum.addError` calls.
  error.source === 'custom'
    ? FIRST_PARTY
    : attributeTelemetry({ url: error.resource?.url, texts: [error.message, error.stack] });

// The RUM SDK discards edits outside its modifiable fields, so only those are rewritten here.
export const beforeSendRum: RumBeforeSend = (event) => {
  const view = asFields(event.view);
  rewrite(view, 'url', redactUrl);
  rewrite(view, 'referrer', redactUrl);
  rewrite(asFields(view?.performance)?.lcp, 'resource_url', redactUrl);

  if (event.type === 'resource') {
    rewrite(event.resource, 'url', redactUrl);
  } else if (event.type === 'action') {
    rewrite(event.action.target, 'name', redactText);
  } else if (event.type === 'long_task') {
    event.long_task.scripts?.forEach((script) => rewrite(script, 'source_url', redactUrl));
  } else if (event.type === 'error') {
    event.context = { ...event.context, attribution: attributeRumError(event.error) };
    rewrite(event.error, 'message', redactText);
    rewrite(event.error, 'stack', redactText);
    rewrite(event.error.resource, 'url', redactUrl);
  }
  return true;
};

export const beforeSendLogs: LogsBeforeSend = (event) => {
  // `logger` logs come from our own `logger`/`datadogLogs.logger` calls.
  event.attribution =
    event.origin === 'logger'
      ? FIRST_PARTY
      : attributeTelemetry({
          url: event.http?.url,
          texts: [event.message, event.error?.message, event.error?.stack],
        });

  rewrite(event.view, 'url', redactUrl);
  rewrite(event.view, 'referrer', redactUrl);
  rewrite(event.http, 'url', redactUrl);
  rewrite(event, 'message', redactText);
  rewrite(event.error, 'message', redactText);
  rewrite(event.error, 'stack', redactText);
  event.error?.causes?.forEach((cause) => {
    rewrite(cause, 'message', redactText);
    rewrite(cause, 'stack', redactText);
  });
  return true;
};
